import { useEffect, useState, type FormEvent } from 'react';
import {
  Archive,
  CheckCircle2,
  Copy,
  ExternalLink,
  Handshake,
  KeyRound,
  Loader2,
  PauseCircle,
  RefreshCw,
  Search,
  ShieldCheck,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { api, ApiRequestError } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

type PartnerApplicationType = 'direct' | 'reseller' | 'integrator' | 'oversight';
type PartnerApplicationStatus = 'pending' | 'active' | 'suspended' | 'rejected' | 'archived';

type PartnerApplicationRow = {
  networkMemberId?: string;
  networkmemberid?: string;
  companyName?: string;
  companyname?: string;
  countryCode?: string | null;
  countrycode?: string | null;
  memberStatus?: PartnerApplicationStatus;
  memberstatus?: PartnerApplicationStatus;
  partnerApplicationType?: PartnerApplicationType;
  partnerapplicationtype?: PartnerApplicationType;
  contactEmail?: string | null;
  contactemail?: string | null;
  contactPhone?: string | null;
  contactphone?: string | null;
  capabilities?: unknown;
  metadata?: unknown;
  createdAt?: string | null;
  createdat?: string | null;
  partnerOrgId?: string | null;
  partnerorgid?: string | null;
  partnerOrgStatus?: string | null;
  partnerorgstatus?: string | null;
  companyType?: string | null;
  companytype?: string | null;
  ownerName?: string | null;
  ownername?: string | null;
  ownerEmail?: string | null;
  owneremail?: string | null;
  routingOwner: string;
  workspacePath: string;
};

type PartnerApplicationSummary = {
  type: PartnerApplicationType;
  status: PartnerApplicationStatus;
  count: number;
  routingOwner: string;
};

type PartnerApplicationQueue = {
  items: PartnerApplicationRow[];
  summary: PartnerApplicationSummary[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  partnerOrgTableAvailable: boolean;
};

type PartnerApplicationReviewResponse = {
  networkMemberId: string;
  status: PartnerApplicationStatus;
  partnerApplicationType: PartnerApplicationType;
  reviewedAt: string;
  partnerOrgUpdated: number;
};

type PartnerGrantType = 'workspace_module' | 'sandbox' | 'api_center' | 'evidence_room' | 'deal_registration' | 'sales_kit' | 'tenant_handoff';
type PartnerGrantAccessLevel = 'read' | 'request' | 'test' | 'manage';
type PartnerGrantDataPolicy = 'own_org' | 'assigned_only' | 'sandbox_only' | 'masked_only' | 'aggregated_only' | 'scoped';

type PartnerGrantPayload = {
  grantType: PartnerGrantType;
  scopeKey: string;
  accessLevel: PartnerGrantAccessLevel;
  dataPolicy: PartnerGrantDataPolicy;
  purpose: string;
};

type PartnerControlResponse = {
  reused: boolean;
  acceptUrl?: string | null;
};

const PARTNER_TYPES: Array<{ value: 'all' | PartnerApplicationType; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'direct', label: 'Direct' },
  { value: 'reseller', label: 'Reseller' },
  { value: 'integrator', label: 'Integrator' },
  { value: 'oversight', label: 'Oversight' },
];

const STATUSES: Array<{ value: PartnerApplicationStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'archived', label: 'Archived' },
];

const TYPE_BADGE: Record<PartnerApplicationType, string> = {
  direct: 'bg-blue-100 text-blue-700 border-blue-200',
  reseller: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  integrator: 'bg-violet-100 text-violet-700 border-violet-200',
  oversight: 'bg-amber-100 text-amber-700 border-amber-200',
};

const STARTER_GRANTS: Record<PartnerApplicationType, PartnerGrantPayload> = {
  direct: {
    grantType: 'workspace_module',
    scopeKey: 'direct:onboarding',
    accessLevel: 'read',
    dataPolicy: 'own_org',
    purpose: 'Starter access for direct customer onboarding workspace',
  },
  reseller: {
    grantType: 'sales_kit',
    scopeKey: 'channel:sales-kit',
    accessLevel: 'read',
    dataPolicy: 'own_org',
    purpose: 'Starter access for channel sales kit and partner enablement',
  },
  integrator: {
    grantType: 'api_center',
    scopeKey: 'integrator:api-center',
    accessLevel: 'read',
    dataPolicy: 'sandbox_only',
    purpose: 'Starter access for API Center and sandbox discovery',
  },
  oversight: {
    grantType: 'evidence_room',
    scopeKey: 'oversight:evidence-room',
    accessLevel: 'read',
    dataPolicy: 'masked_only',
    purpose: 'Starter access for masked oversight evidence room',
  },
};

function parseRecord(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : {};
    } catch {
      return {};
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function parseStringList(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
    } catch {
      return value ? [value] : [];
    }
  }
  return [];
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function rowType(row: PartnerApplicationRow): PartnerApplicationType {
  return row.partnerApplicationType ?? row.partnerapplicationtype ?? 'direct';
}

function rowCompany(row: PartnerApplicationRow): string {
  return row.companyName ?? row.companyname ?? 'Unknown company';
}

function rowStatus(row: PartnerApplicationRow): PartnerApplicationStatus {
  return row.memberStatus ?? row.memberstatus ?? 'pending';
}

function rowNetworkMemberId(row: PartnerApplicationRow): string | null {
  return row.networkMemberId ?? row.networkmemberid ?? null;
}

function rowCreatedAt(row: PartnerApplicationRow): string | null {
  return row.createdAt ?? row.createdat ?? null;
}

function rowCountry(row: PartnerApplicationRow): string | null {
  return row.countryCode ?? row.countrycode ?? null;
}

function rowPartnerOrgId(row: PartnerApplicationRow): string | null {
  return row.partnerOrgId ?? row.partnerorgid ?? null;
}

function rowCompanyType(row: PartnerApplicationRow): string | null {
  return row.companyType ?? row.companytype ?? null;
}

function rowOwnerName(row: PartnerApplicationRow): string | null {
  return row.ownerName ?? row.ownername ?? null;
}

function rowOwnerEmail(row: PartnerApplicationRow): string | null {
  return row.ownerEmail ?? row.owneremail ?? null;
}

function rowContactEmail(row: PartnerApplicationRow): string | null {
  return row.contactEmail ?? row.contactemail ?? null;
}

function rowContactPhone(row: PartnerApplicationRow): string | null {
  return row.contactPhone ?? row.contactphone ?? null;
}

function formatDate(value: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' });
}

function buildQuery(type: 'all' | PartnerApplicationType, status: PartnerApplicationStatus, search: string) {
  const params = new URLSearchParams({ status, limit: '50' });
  if (type !== 'all') params.set('type', type);
  if (search.trim()) params.set('q', search.trim());
  return params.toString();
}

export default function PartnerApplicationsPage() {
  const [type, setType] = useState<'all' | PartnerApplicationType>('all');
  const [status, setStatus] = useState<PartnerApplicationStatus>('pending');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<PartnerApplicationQueue | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api.get<PartnerApplicationQueue>(`/partner-workspace/internal/applications?${buildQuery(type, status, search)}`)
      .then(setData)
      .catch((err: unknown) => {
        const message = err instanceof ApiRequestError
          ? `${err.code}: ${err.message}`
          : 'Failed to load partner applications';
        setError(message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [type, status]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    load();
  };

  const reviewApplication = (memberId: string | null, nextStatus: PartnerApplicationStatus) => {
    if (!memberId) {
      setError('Missing network member id; cannot update application status.');
      return;
    }

    setUpdatingMemberId(memberId);
    setError(null);
    setInviteLink(null);
    api.patch<PartnerApplicationReviewResponse>(
      `/partner-workspace/internal/applications/${encodeURIComponent(memberId)}/status`,
      { status: nextStatus },
    )
      .then(() => load())
      .catch((err: unknown) => {
        const message = err instanceof ApiRequestError
          ? `${err.code}: ${err.message}`
          : 'Failed to update partner application status';
        setError(message);
      })
      .finally(() => setUpdatingMemberId(null));
  };

  const prepareInvite = (row: PartnerApplicationRow) => {
    const memberId = rowNetworkMemberId(row);
    const orgId = rowPartnerOrgId(row);
    const email = rowOwnerEmail(row) ?? rowContactEmail(row);
    if (!orgId || !email) {
      setError('Missing partner org or email; cannot prepare invite.');
      return;
    }

    setUpdatingMemberId(memberId);
    setError(null);
    setNotice(null);
    setInviteLink(null);
    api.post<PartnerControlResponse>(
      `/partner-workspace/internal/orgs/${encodeURIComponent(orgId)}/invites`,
      { email, purpose: 'Partner workspace onboarding' },
    )
      .then((res) => {
        setInviteLink(res.acceptUrl ?? null);
        setNotice(res.reused
          ? 'Existing pending partner invite reused. Accept link is not re-shown for stored token safety.'
          : 'Pending partner invite prepared. No email was sent.');
      })
      .catch((err: unknown) => {
        const message = err instanceof ApiRequestError
          ? `${err.code}: ${err.message}`
          : 'Failed to prepare partner invite';
        setError(message);
      })
      .finally(() => setUpdatingMemberId(null));
  };

  const grantStarterAccess = (row: PartnerApplicationRow) => {
    const memberId = rowNetworkMemberId(row);
    const orgId = rowPartnerOrgId(row);
    if (!orgId) {
      setError('Missing partner org; cannot create starter access grant.');
      return;
    }

    setUpdatingMemberId(memberId);
    setError(null);
    setNotice(null);
    setInviteLink(null);
    api.post<PartnerControlResponse>(
      `/partner-workspace/internal/orgs/${encodeURIComponent(orgId)}/access-grants`,
      STARTER_GRANTS[rowType(row)],
    )
      .then((res) => setNotice(res.reused ? 'Existing active starter grant reused.' : 'Starter access grant created. No credential was issued.'))
      .catch((err: unknown) => {
        const message = err instanceof ApiRequestError
          ? `${err.code}: ${err.message}`
          : 'Failed to create starter access grant';
        setError(message);
      })
      .finally(() => setUpdatingMemberId(null));
  };

  const typeCounts = new Map<PartnerApplicationType, number>();
  for (const item of data?.summary ?? []) {
    if (item.status === status) typeCounts.set(item.type, (typeCounts.get(item.type) ?? 0) + item.count);
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <Handshake className="h-3.5 w-3.5" />
            partner.boox.network
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Partner Applications</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Internal routing board for direct customers, channel partners, integrators, and oversight bodies. Status review is enabled; invite, credential, and access grant creation remain separate controlled steps.
          </p>
        </div>
        <Button onClick={load} disabled={loading} className="gap-2">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {(['direct', 'reseller', 'integrator', 'oversight'] as PartnerApplicationType[]).map((key) => (
          <Card key={key} className={cn(type === key && 'border-primary shadow-sm')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Badge className={cn('capitalize', TYPE_BADGE[key])}>{key}</Badge>
                <span className="text-2xl font-bold">{typeCounts.get(key) ?? 0}</span>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Owner: {key === 'direct' ? 'sales' : key === 'reseller' ? 'channel' : key === 'integrator' ? 'solution engineering' : 'founder / audit'}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Queue Filters
          </CardTitle>
          <CardDescription>
            Reads `/partner-workspace/internal/applications`; status actions require platform admin access.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="grid gap-3 md:grid-cols-[180px_180px_1fr_auto]">
            <select
              value={type}
              onChange={(event) => setType(event.target.value as 'all' | PartnerApplicationType)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {PARTNER_TYPES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value as PartnerApplicationStatus)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {STATUSES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search company, email, phone..."
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="secondary">Search</Button>
          </form>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-800">{error}</CardContent>
        </Card>
      )}

      {notice && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="space-y-3 p-4 text-sm text-emerald-800">
            <div>{notice}</div>
            {inviteLink && (
              <div className="flex flex-col gap-2 rounded-lg border border-emerald-200 bg-white/70 p-3 md:flex-row md:items-center md:justify-between">
                <a href={inviteLink} target="_blank" rel="noreferrer" className="break-all font-mono text-xs text-emerald-900 hover:underline">
                  {inviteLink}
                </a>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-2"
                  onClick={() => navigator.clipboard?.writeText(inviteLink)}
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-base">Application Queue</CardTitle>
            <CardDescription>
              {data ? `${data.total.toLocaleString()} applications · partner_orgs table ${data.partnerOrgTableAvailable ? 'available' : 'not available'}` : 'Loading queue...'}
            </CardDescription>
          </div>
          <Badge variant="outline">{status}</Badge>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex min-h-[220px] items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading partner applications...
            </div>
          ) : !data?.items.length ? (
            <div className="flex min-h-[220px] items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
              No partner applications found for this filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Intent</TableHead>
                    <TableHead>Routing</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Workspace</TableHead>
                    <TableHead>Review</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((row) => {
                    const metadata = parseRecord(row.metadata);
                    const partnerType = rowType(row);
                    const contactName = readString(metadata['contact_name']) ?? rowOwnerName(row) ?? '-';
                    const intent = readString(metadata['intent_summary']) ?? '-';
                    const program = readString(metadata['program_interest']) ?? rowCompanyType(row) ?? '-';
                    const areas = parseStringList(row.capabilities).slice(0, 3);
                    const orgId = rowPartnerOrgId(row);
                    const memberId = rowNetworkMemberId(row);
                    const currentStatus = rowStatus(row);
                    const isUpdating = updatingMemberId === memberId;

                    return (
                      <TableRow key={`${memberId ?? 'no-member'}-${orgId ?? 'no-org'}`}>
                        <TableCell className="min-w-[220px]">
                          <div className="font-medium">{rowCompany(row)}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{rowCountry(row) ?? '-'} · {currentStatus}</div>
                          {areas.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {areas.map((area) => <Badge key={area} variant="secondary" className="text-[10px]">{area}</Badge>)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={cn('capitalize', TYPE_BADGE[partnerType])}>{partnerType}</Badge>
                        </TableCell>
                        <TableCell className="min-w-[180px] text-sm">
                          <div>{contactName}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{rowContactEmail(row) ?? '-'}</div>
                          <div className="text-xs text-muted-foreground">{rowContactPhone(row) ?? '-'}</div>
                        </TableCell>
                        <TableCell className="min-w-[280px]">
                          <div className="text-sm font-medium">{program}</div>
                          <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{intent}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{row.routingOwner.replaceAll('_', ' ')}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{orgId ? `Org ${orgId.slice(0, 8)}` : 'No org shell'}</div>
                        </TableCell>
                        <TableCell className="min-w-[150px] text-xs text-muted-foreground">
                          {formatDate(rowCreatedAt(row))}
                        </TableCell>
                        <TableCell>
                          <a
                            href={row.workspacePath}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                          >
                            Preview <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </TableCell>
                        <TableCell className="min-w-[260px]">
                          <div className="flex flex-wrap gap-2">
                            {currentStatus !== 'active' && (
                              <Button
                                size="sm"
                                variant="secondary"
                                className="h-8 gap-1"
                                disabled={isUpdating}
                                onClick={() => reviewApplication(memberId, 'active')}
                              >
                                {isUpdating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                                Activate
                              </Button>
                            )}
                            {currentStatus === 'active' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1"
                                disabled={isUpdating}
                                onClick={() => reviewApplication(memberId, 'suspended')}
                              >
                                {isUpdating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PauseCircle className="h-3.5 w-3.5" />}
                                Suspend
                              </Button>
                            )}
                            {currentStatus === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                                disabled={isUpdating}
                                onClick={() => reviewApplication(memberId, 'rejected')}
                              >
                                {isUpdating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                                Reject
                              </Button>
                            )}
                            {currentStatus !== 'archived' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 gap-1"
                                disabled={isUpdating}
                                onClick={() => reviewApplication(memberId, 'archived')}
                              >
                                {isUpdating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
                                Archive
                              </Button>
                            )}
                            {currentStatus === 'active' && orgId && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1"
                                disabled={isUpdating}
                                onClick={() => prepareInvite(row)}
                              >
                                {isUpdating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                                Prep invite
                              </Button>
                            )}
                            {currentStatus === 'active' && orgId && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1"
                                disabled={isUpdating}
                                onClick={() => grantStarterAccess(row)}
                              >
                                {isUpdating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
                                Starter grant
                              </Button>
                            )}
                          </div>
                          <div className="mt-2 text-[11px] text-muted-foreground">
                            Invite is pending only. Starter grant creates scope, not credential.
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
